/**
 * +field: field kills batched for the `fieldKills` intent. The HUD moves on every kill (XP
 * predicted locally with progression's killXp); the batch goes out every FLUSH_MS, or at once when
 * the predicted XP reaches the next level (so the level-up lands on the kill that earned it).
 */
import type { DeepReadonly, SimEvent, World } from '@keystone/sim';
import { killXp } from '@keystone/progression';

export interface FieldKill {
  npcId: string;
  level: number;
  elite?: boolean;
}

/** Send the batch at least this often while kills are waiting. */
export const KILL_FLUSH_MS = 1500;
/** Hard cap on one report (the server's burst budget drops the excess anyway). */
export const KILL_BATCH_MAX = 40;

export class KillBatch {
  private kills: FieldKill[] = [];
  private since: number | null = null;
  /** XP predicted for the kills waiting in this batch. */
  predicted = 0;
  /** The waiting kills were made by Auto (a batch never mixes Auto and manual kills: see `add`). */
  auto = false;

  get size(): number {
    return this.kills.length;
  }

  /** True when a kill made with Auto `auto` cannot join the waiting batch (flush it first). */
  mixes(auto: boolean): boolean {
    return this.kills.length > 0 && this.auto !== auto;
  }

  /** Queue a kill (its predicted XP for the hero's level; Auto kills pay the same). */
  add(kill: FieldKill, heroLevel: number, now: number, auto = false): number {
    if (!this.kills.length) this.auto = auto;
    const xp = killXp(kill.level, heroLevel, !!kill.elite);
    this.kills.push(kill);
    this.predicted += xp;
    this.since ??= now;
    return xp;
  }

  /** Time to send: the flush interval passed, the batch is full, or a level-up is due (`xpInLevel` + predicted ≥ `need`). */
  due(now: number, xpInLevel: number, need: number): boolean {
    if (!this.kills.length) return false;
    if (this.kills.length >= KILL_BATCH_MAX) return true;
    if (need > 0 && xpInLevel + this.predicted >= need) return true;
    return this.since !== null && now - this.since >= KILL_FLUSH_MS;
  }

  /** Take the batch (resets it). */
  drain(): { kills: FieldKill[]; predicted: number; auto: boolean } {
    const out = { kills: this.kills, predicted: this.predicted, auto: this.auto };
    this.kills = [];
    this.predicted = 0;
    this.since = null;
    return out;
  }
}

/** Levelled enemy kills in a step's events (deaths of enemy NPC units that carry a level). */
export function levelledKills(events: readonly SimEvent[], world: DeepReadonly<World>): FieldKill[] {
  let out: FieldKill[] | null = null;
  for (const e of events) {
    if (e.type !== 'death') continue;
    const u = world.units[e.targetID];
    if (!u || u.faction !== 'enemy' || !u.npcId || typeof u.level !== 'number') continue;
    (out ??= []).push(u.elite ? { npcId: u.npcId, level: u.level, elite: true } : { npcId: u.npcId, level: u.level });
  }
  return out ?? NONE;
}
const NONE: FieldKill[] = [];
